// ドキュメント関連のハンドラー
import { findItemById } from '../utils/item-finder.js';
import { getSettingTitle, getSettingDescription, getCategoryTitle, getSectionTitle } from '../utils/i18n.js';
import { t } from '../utils/i18n.js';

/**
 * Docsドロワーを開く
 */
export function openDocs(itemId: string): void {
    const meta = findItemById(itemId);
    if (!meta) return;

    const { category, section, item } = meta;
    const backdrop = document.getElementById('docsDrawerBackdrop');
    const drawer = document.getElementById('docsDrawer');
    const titleEl = document.getElementById('docsDrawerTitle');
    const bodyEl = document.getElementById('docsDrawerBody');

    if (!backdrop || !drawer || !titleEl || !bodyEl) return;

    titleEl.textContent = t('docsDrawerTitle', { 
        itemTitle: getSettingTitle(item.id, item.title), 
        categoryTitle: getCategoryTitle(category.title, category.title) 
    });

    const codeBlock = (item.code || '').trim()
        ? `<pre class="bg-black/60 border border-gray-700 rounded px-3 py-2 text-[11px] text-green-400 overflow-x-auto whitespace-pre">${(item.code || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`
        : `<p class="text-[11px] text-gray-500">${t('docsNoCodeOutput')}</p>`;

    // MarkdownをHTMLに変換する関数
    function markdownToHtml(markdown: string): string {
        if (typeof window !== 'undefined' && window.marked && window.marked.parse) {
            // marked.jsのオプション設定
            window.marked.setOptions({
                breaks: true, // 改行を<br>に変換
                gfm: true, // GitHub Flavored Markdownを有効化
            });
            return window.marked.parse(markdown);
        }
        // marked.jsが読み込まれていない場合は、改行を<br>に変換して返す
        return markdown.replace(/\n/g, '<br>');
    }

    // 詳細説明のセクションを生成
    let detailedSection = '';
    if ((item as any).detailedDescription) {
        const markdownHtml = markdownToHtml((item as any).detailedDescription);
        detailedSection = `
        <section>
            <h4 class="text-xs font-semibold text-gray-300 mb-2">${t('docsDetailedDescription')}</h4>
            <div class="text-[12px] text-gray-200 leading-relaxed mb-3 markdown-content">
                ${markdownHtml}
            </div>
            ${(item as any).commandExplanation ? `
            <div class="mb-3">
                <h5 class="text-[11px] font-semibold text-gray-300 mb-2">${t('docsCommandExplanation')}</h5>
                <div class="text-[12px] text-gray-200 leading-relaxed markdown-content">
                    ${typeof (item as any).commandExplanation === 'string' 
                        ? markdownToHtml((item as any).commandExplanation)
                        : markdownToHtml(Object.entries((item as any).commandExplanation).map(([key, value]: [string, unknown]) => `- **${key}**: ${value}`).join('\n'))
                    }
                </div>
            </div>
            ` : ''}
            ${(item as any).featureExplanation ? `
            <div>
                <h5 class="text-[11px] font-semibold text-gray-300 mb-2">${t('docsWhatIs', { itemTitle: getSettingTitle(item.id, item.title) })}</h5>
                <div class="text-[12px] text-gray-200 leading-relaxed markdown-content">
                    ${markdownToHtml((item as any).featureExplanation)}
                </div>
            </div>
            ` : ''}
        </section>
        `;
    }

    bodyEl.innerHTML = `
        <section>
            <h4 class="text-xs font-semibold text-gray-300 mb-1">${t('docsOverview')}</h4>
            <p class="text-[12px] text-gray-200">
                ${getSettingDescription(item.id, item.description)}
            </p>
        </section>
        ${detailedSection}
        <section>
            <h4 class="text-xs font-semibold text-gray-300 mb-1">${t('docsCategory')} / ${t('docsSection')}</h4>
            <p class="text-[11px] text-gray-400">
                ${t('docsCategory')} <span class="text-gray-200">${getCategoryTitle(category.title, category.title)}</span><br>
                ${t('docsSection')} <span class="text-gray-200">${getSectionTitle(section.title, section.title)}</span>
            </p>
        </section>
        <section>
            <h4 class="text-xs font-semibold text-gray-300 mb-1">${t('docsGeneratedCode')}</h4>
            ${codeBlock}
        </section>
        <section>
            <h4 class="text-xs font-semibold text-gray-300 mb-2">${t('docsOverview')}</h4>
            <div class="space-y-2 text-[11px] text-gray-300 leading-relaxed">
                ${item.requiresAdmin ? `
                <div class="bg-red-900/20 border-l-2 border-red-600 pl-2 py-1">
                    <strong class="text-red-400">${t('docsAdminRequired')}</strong> ${t('docsAdminRequiredDesc')}
                </div>
                ` : ''}
                ${(item as any).requiresReboot ? `
                <div class="bg-orange-900/20 border-l-2 border-orange-600 pl-2 py-1">
                    <strong class="text-orange-400">${t('docsRebootRequired')}</strong> ${t('docsRebootRequiredDesc')}
                </div>
                ` : ''}
                ${item.revertCode ? `
                <div class="bg-blue-900/20 border-l-2 border-blue-600 pl-2 py-1">
                    <strong class="text-blue-400">${t('docsRevertAvailable')}</strong> ${t('docsRevertAvailableDesc')}
                </div>
                ` : ''}
                ${(item as any).isDynamicList ? `
                <div class="bg-purple-900/20 border-l-2 border-purple-600 pl-2 py-1">
                    <strong class="text-purple-400">${t('docsDynamicSetting')}</strong> ${t('docsDynamicSettingDesc')}
                </div>
                ` : ''}
            </div>
        </section>
    `;

    backdrop.classList.remove('hidden');
    drawer.classList.remove('hidden');
}

/**
 * Docsドロワーを閉じる
 */
export function closeDocs(): void {
    const backdrop = document.getElementById('docsDrawerBackdrop');
    const drawer = document.getElementById('docsDrawer');
    if (backdrop) backdrop.classList.add('hidden');
    if (drawer) drawer.classList.add('hidden');
}

